import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Smartphone, Monitor, Wifi, Camera, Tv } from 'lucide-react';

// Define services array
const services = [
  {
    icon: Shield,
    title: 'Virus & Malware Removal',
    description: 'Professional virus and malware removal services',
    category: 'Computer Repair',
    image: 'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?auto=format&fit=crop&w=800&q=60',
    features: ['Deep System Scan', 'Malware Removal', 'Security Updates', 'Performance Optimization']
  },
  {
    icon: Smartphone,
    title: 'Phone Repair',
    description: 'Expert iPhone and Samsung screen repair',
    category: 'Phone',
    image: 'https://images.unsplash.com/photo-1597740985671-2a8a3b80502e?auto=format&fit=crop&w=800&q=60',
    features: ['Screen Replacement', 'Battery Replacement', 'Water Damage Repair', 'Camera Repair']
  },
  {
    icon: Monitor,
    title: 'PC/Laptop Repair',
    description: 'Comprehensive computer repair services',
    category: 'Computer Repair',
    image: 'https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?auto=format&fit=crop&w=800&q=60',
    features: ['Hardware Repair', 'Software Issues', 'Data Recovery', 'System Upgrades']
  },
  {
    icon: Wifi,
    title: 'Network Support',
    description: 'WiFi and network troubleshooting',
    category: 'Network Setup',
    image: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&w=800&q=60',
    features: ['WiFi Setup', 'Network Security', 'Speed Optimization', 'Device Configuration']
  },
  {
    icon: Camera,
    title: 'Security Installation',
    description: 'Professional camera installation services',
    category: 'Security Installation',
    image: 'https://images.unsplash.com/photo-1557597774-9d273605dfa9?auto=format&fit=crop&w=800&q=60',
    features: ['Camera Installation', 'Motion Sensors', '24/7 Monitoring', 'Mobile App Access']
  },
  {
    icon: Tv,
    title: 'TV Mounting',
    description: 'Professional TV mounting and setup',
    category: 'TV Mounting',
    image: 'https://images.unsplash.com/photo-1593784991095-a205069470b6?auto=format&fit=crop&w=800&q=60',
    features: ['Wall Mount Installation', 'Cable Management', 'Sound System Setup', 'Smart TV Configuration']
  }
];

const words = ['Reliable', 'Professional', 'Expert', 'Trusted'];

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [hoveredService, setHoveredService] = useState<string | null>(null);
  const [touchedService, setTouchedService] = useState<string | null>(null);
  const [currentWord, setCurrentWord] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [contentLoaded, setContentLoaded] = useState(false);

  useEffect(() => {
    // Mark content as loaded to remove loading indicator
    const timer = setTimeout(() => {
      setContentLoaded(true);
      // Remove loading indicator from DOM
      const loadingIndicator = document.getElementById('loading-indicator');
      if (loadingIndicator && loadingIndicator.parentNode) {
        loadingIndicator.parentNode.removeChild(loadingIndicator);
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const wordInterval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentWord((prev) => (prev + 1) % words.length);
        setIsVisible(true);
      }, 500);
    }, 3000);

    return () => clearInterval(wordInterval);
  }, []);

  const handleServiceClick = (service: typeof services[0]) => {
    navigate('/submit-ticket', { state: { selectedCategory: service.category } });
  };

  const handleTouchStart = (service: typeof services[0]) => {
    if (touchedService === service.title) {
      handleServiceClick(service);
    } else {
      setTouchedService(service.title);
      setHoveredService(service.title);
    }
  };

  const handleTouchEnd = () => {
    setTimeout(() => {
      setTouchedService(null);
    }, 300);
  };

  return (
    <div>
      {/* Hero Section - Optimized background image loading */}
      <section 
        className="relative bg-secondary text-white py-20"
        style={{
          backgroundImage: `
            linear-gradient(
              rgba(0, 0, 0, 0.7),
              rgba(0, 0, 0, 0.7)
            ),
            url('https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=50')`
        }}
      >
        <div 
          className="absolute inset-0"
          style={{
            backgroundColor: 'rgba(255, 107, 0, 0.2)',
            mixBlendMode: 'overlay'
          }}
        />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Professional Tech Support & Repair Services
            </h1>
            <p className="text-xl mb-8">
              Expert solutions for all your technology needs
            </p>
            <Link
              to="/submit-ticket"
              className="inline-block bg-primary text-white px-8 py-3 rounded-md text-lg font-semibold hover:bg-primary/90 transition-colors transform hover:scale-105 duration-200"
            >
              Get Help Now
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section - With optimized loading */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Our Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                onMouseEnter={() => setHoveredService(service.title)}
                onMouseLeave={() => {
                  if (!touchedService) {
                    setHoveredService(null);
                  }
                }}
                onTouchStart={() => handleTouchStart(service)}
                onTouchEnd={handleTouchEnd}
                className={`group relative overflow-hidden rounded-lg shadow-lg cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-xl bg-white ${
                  touchedService === service.title ? 'scale-105 shadow-xl' : ''
                }`}
              >
                <div className="relative h-48">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                    loading={index > 2 ? 'lazy' : 'eager'}
                  />
                  <div className={`absolute inset-0 bg-black transition-all duration-300 ${
                    hoveredService === service.title ? 'bg-opacity-40' : 'bg-opacity-50'
                  }`}></div>
                  <service.icon className="absolute top-4 left-4 w-8 h-8 text-white" />
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-gray-900">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  
                  <div className={`space-y-2 transition-all duration-300 ${
                    hoveredService === service.title || touchedService === service.title
                      ? 'max-h-40 opacity-100'
                      : 'max-h-0 opacity-0'
                  }`}>
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary mr-2"></div>
                        {feature}
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => handleServiceClick(service)}
                    className={`mt-4 w-full bg-primary text-white px-6 py-2 rounded-lg transition-all duration-300 transform ${
                      hoveredService === service.title || touchedService === service.title
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-2'
                    }`}
                  >
                    Get Service
                  </button>
                </div>

                {touchedService === service.title && (
                  <div className="absolute bottom-2 right-2 text-xs text-gray-500">
                    Tap again to select
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Technical Support?</h2>
          <p className="text-xl mb-8">
            Submit a ticket or contact us directly for immediate assistance
          </p>
          <div className="space-x-4">
            <Link
              to="/submit-ticket"
              className="bg-white text-primary px-8 py-3 rounded-md text-lg font-semibold hover:bg-gray-100 transition-colors transform hover:scale-105 duration-200 inline-block"
            >
              Submit a Ticket
            </Link>
            <Link
              to="/contact"
              className="bg-secondary text-white px-8 py-3 rounded-md text-lg font-semibold hover:bg-secondary/90 transition-colors transform hover:scale-105 duration-200 inline-block"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;